import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { filterActiveContracts } from "@/utils/contractUtils";
import { useProperties } from "@/hooks/useProperties";
import { useBulkUpdateBusinessIncome } from "@/hooks/useBulkUpdateBusinessIncome";
import { BUSINESS_INCOME_TYPES, BUSINESS_PAYMENT_TYPES } from "@/constants/businessIncomes";
import { useState, useEffect, useMemo } from "react";

interface BulkEditBusinessIncomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
  onSuccess: () => void;
}

const NO_CHANGE = "__NO_CHANGE__";

const BulkEditBusinessIncomeModal = ({
  isOpen,
  onClose,
  selectedIds,
  onSuccess,
}: BulkEditBusinessIncomeModalProps) => {
  const { data: organizations = [] } = useOrganizations();
  const { data: allContracts = [] } = useContracts();
  const { data: allProperties = [] } = useProperties();
  const bulkUpdateMutation = useBulkUpdateBusinessIncome();

  const [organizationId, setOrganizationId] = useState<string>(NO_CHANGE);
  const [propertyId, setPropertyId] = useState<string>(NO_CHANGE);
  const [incomeType, setIncomeType] = useState<string>(NO_CHANGE);
  const [paymentType, setPaymentType] = useState<string>(NO_CHANGE);

  // Filter properties based on selected organization
  const filteredProperties = useMemo(() => {
    if (organizationId === NO_CHANGE || !organizationId) {
      return [];
    }

    const activeContracts = filterActiveContracts(allContracts).filter(
      (contract) => contract.organization_id === organizationId
    );

    // Get unique property IDs from active contracts
    const propertyIds = activeContracts.map((contract) => contract.property_id);
    
    // Filter properties by IDs
    return allProperties.filter((property) => propertyIds.includes(property.id));
  }, [organizationId, allContracts, allProperties]);

  // Reset property when organization changes
  useEffect(() => {
    if (organizationId !== NO_CHANGE) {
      setPropertyId(NO_CHANGE);
    }
  }, [organizationId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOrganizationId(NO_CHANGE);
      setPropertyId(NO_CHANGE);
      setIncomeType(NO_CHANGE);
      setPaymentType(NO_CHANGE);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const updates: any = {};

    if (organizationId !== NO_CHANGE) {
      updates.organization_id = organizationId;
    }

    if (propertyId !== NO_CHANGE) {
      updates.property_id = propertyId === "none" ? null : propertyId;
    }

    if (incomeType !== NO_CHANGE) {
      updates.income_type = incomeType;
    }

    if (paymentType !== NO_CHANGE) {
      updates.payment_type = paymentType;
    }

    try {
      await bulkUpdateMutation.mutateAsync({
        incomeIds: selectedIds,
        updates,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error in bulk edit:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Editar {selectedIds.length} Ingreso(s)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Organization */}
          <div className="space-y-2">
            <Label>Organización</Label>
            <Select value={organizationId} onValueChange={setOrganizationId}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Property (cascaded by organization) */}
          <div className="space-y-2">
            <Label>Propiedad</Label>
            <Select 
              value={propertyId} 
              onValueChange={setPropertyId}
              disabled={organizationId === NO_CHANGE}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                <SelectItem value="none">Sin propiedad específica</SelectItem>
                {filteredProperties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.shortName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {organizationId === NO_CHANGE && (
              <p className="text-xs text-muted-foreground">
                Selecciona una organización primero
              </p>
            )}
          </div>

          {/* Income Type */}
          <div className="space-y-2">
            <Label>Tipo de Ingreso</Label>
            <Select value={incomeType} onValueChange={setIncomeType}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {BUSINESS_INCOME_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Type */}
          <div className="space-y-2">
            <Label>Tipo de Pago</Label>
            <Select value={paymentType} onValueChange={setPaymentType}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {BUSINESS_PAYMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={bulkUpdateMutation.isPending}
            className="bg-brand-primary hover:bg-brand-primary-hover"
          >
            {bulkUpdateMutation.isPending ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEditBusinessIncomeModal;
